/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { SnackbarKey, useSnackbar } from 'notistack';
import { memo, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../store';
import { removeSnackbar } from '../../store/notifier/actions';

const mapState = (state: RootState) => ({
  notifier: state.notifier,
});

const mapDispatch = {
  removeSnackbar,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

const NotifierComponent = memo<PropsFromRedux>(props => {
  const { notifier, removeSnackbar } = props;
  const { notifications } = notifier;

  const displayed = useRef<SnackbarKey[]>([]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const storeDisplayed = (id: SnackbarKey) => {
    displayed.current = [...displayed.current, id];
  };

  const removeDisplayed = (id: SnackbarKey) => {
    displayed.current = [...displayed.current.filter(key => id !== key)];
  };

  useEffect(() => {
    notifications.forEach(({ key, message, options = {}, dismissed = false }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key);
        return;
      }

      // do nothing if snackbar is already displayed
      if (displayed.current.includes(`${key}`)) {
        return;
      }
      // display snackbar using notistack
      enqueueSnackbar(message, {
        key,
        ...options,
        onClose: (event, reason, myKey) => {
          if (options.onClose) {
            options.onClose(event, reason, myKey);
          }
        },
        onExited: (event, myKey) => {
          // removen this snackbar from redux store
          removeSnackbar(myKey as SnackbarKey);
          removeDisplayed(myKey as SnackbarKey);
        },
      });

      // keep track of snackbars that we've displayed
      storeDisplayed(key as SnackbarKey);
    });
  }, [notifications, closeSnackbar, enqueueSnackbar, removeSnackbar]);

  return null;
});

export const Notifier = connector(NotifierComponent);
